/**
 * Task B1 — the CANONICAL view vocabulary for multi-view templates (Wave B).
 *
 * This list MIRRORS the cloud option-group source of truth
 * (`cloud/app/options/constants.py:9` — `ALL_VIEWS`). Keep the two in lockstep: the
 * cloud persists a template's per-view baked assets keyed by these `view_type` strings,
 * and the client resolves/renders them (see `bakedSync.ts` `refsForView`/`mergeViewRefs`).
 */

/** Every built-in view, in canonical order (mirror of the cloud `ALL_VIEWS`). */
export const ALL_VIEWS = [
  "back",
  "front",
  "left",
  "right",
  "floating",
  "camera",
  "isometric",
] as const;

/**
 * A view identifier. One of the built-in `ALL_VIEWS` OR any other string — cloud option
 * groups can add customer-specific view choices, so this stays an OPEN string union (the
 * `string & {}` keeps editor autocomplete for the known views without closing the set).
 */
export type ViewType = (typeof ALL_VIEWS)[number] | (string & {});

/**
 * The PRIMARY view. Its baked assets live in the legacy FLAT `baked_assets` fields (every
 * template authored before multi-view is a back-only template), so old app builds keep
 * rendering it. Everything else lives in the `views` map. See `BakedAssetRefsMap`.
 */
export const PRIMARY_VIEW = "back";

/** Title-case each word for a human view label (e.g. "front" → "Front"). Mirrors the local
 *  helper in `export/refs.ts:77` — mirrored (not imported) so `templates/` doesn't take a
 *  dependency on `export/` just for a one-liner, and because that copy isn't exported. */
function titleCase(label: string): string {
  return label.replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Explicit human labels for the built-in views. */
const KNOWN_LABELS: Record<(typeof ALL_VIEWS)[number], string> = {
  back: "Back",
  front: "Front",
  left: "Left",
  right: "Right",
  floating: "Floating",
  camera: "Camera",
  isometric: "Isometric",
};

/**
 * Human label for a view. Backed by a Proxy so any view — built-in OR a customer-added
 * cloud option not in `KNOWN_LABELS` — resolves to a sensible title-cased fallback instead
 * of `undefined`. Typed as `Record<string, string>` so callers can index it by any string.
 */
export const VIEW_LABEL: Record<string, string> = new Proxy(KNOWN_LABELS, {
  get(target, prop) {
    if (typeof prop !== "string") return Reflect.get(target, prop); // symbol props (inspection) pass through
    if (prop in target) return target[prop as (typeof ALL_VIEWS)[number]];
    return titleCase(prop);
  },
}) as Record<string, string>;
